import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LobbyList from '../components/LobbyList';
import useLobby from '../hooks/useLobby';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext'; // Import useSocket to show connection status
import PlayerInfoBanner from '../components/PlayerInfoBanner'; // Import PlayerInfoBanner

/**
 * @file Lobby.tsx
 * @description
 * Lobby page for creating and joining games in real time.
 * Uses the useLobby hook to fetch initial games, subscribe to updates, and perform actions.
 * Displays a player info banner and logout button at the top.
 */

const Lobby: React.FC = () => {
  // Use the useLobby hook for all lobby state and actions
  const {
      waitingGames,
      startedGames,
      createGame,
      joinGame,
      isLoadingInitial,
      error: lobbyError,
      refetchGames, // Get the refetch function
    } = useLobby();
  const { playerName, isAuthenticated } = useAuth(); // Get player name and auth state
  const { isConnected: isSocketConnected, error: socketError } = useSocket(); // Get socket status and error

  const navigate = useNavigate();

  const [joinCode, setJoinCode] = useState('');
  const [privateMode, setPrivateMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false); // State to prevent double-clicks
  const [isJoining, setIsJoining] = useState(false); // State to prevent double-clicks
  const [currentJoinCode, setCurrentJoinCode] = useState(''); // Track the code currently being joined


  // Combine errors from lobby hook and socket context
  const combinedError = lobbyError || socketError;


  /**
   * Create a public game via the hook, join it, then navigate into it.
   */
  const handleCreatePublic = async () => {
    if (isCreating || isJoining || !isSocketConnected || isAuthenticated !== true) return; // Prevent if busy, socket down, or not authenticated
    setIsCreating(true);
    console.log('Attempting to create a PUBLIC game...');
    try {
      const code = await createGame(true); // createGame now handles the REST call AND socket join
      console.log(`Public game created and joined successfully. Code: ${code}`);
      // Navigate to the game room page using the returned code
      navigate(`/game/${code}`);
    } catch (error) {
      console.error('Error creating public game:', error);
      alert(`Failed to create public game: ${error}`); // Basic error display
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Create a private game via the hook, join it, then navigate into it.
   */
  const handleCreatePrivate = async () => {
    if (isCreating || isJoining || !isSocketConnected || isAuthenticated !== true) return; // Prevent if busy, socket down, or not authenticated
    setIsCreating(true);
     setPrivateMode(false); // Hide the extra button after clicking confirm
    console.log('Attempting to create a PRIVATE game...');
    try {
      const code = await createGame(false); // createGame now handles the REST call AND socket join
      console.log(`Private game created and joined successfully. Code: ${code}`);
      // Navigate to the game room page using the returned code
      navigate(`/game/${code}`);
    } catch (error) {
      console.error('Error creating private game:', error);
       alert(`Failed to create private game: ${error}`); // Basic error display
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Join the specified game code via the hook and navigate into it.
   * Used by LobbyList items and the private join input.
   */
  const handleJoin = async (code: string) => {
     if (isCreating || isJoining || !isSocketConnected || isAuthenticated !== true) return; // Prevent if busy, socket down, or not authenticated

     // Prevent joining if player name is missing (should be handled by AuthContext/Login, but safety)
     if (!playerName) {
         alert('Please ensure you have a player name.'); // This shouldn't happen if AuthContext works
         return;
     }

     setIsJoining(true);
     setCurrentJoinCode(code); // Indicate which code is being joined
     console.log(`Attempting to join game code: ${code} via socket...`);

    try {
      await joinGame(code); // joinGame now handles the socket event and state syncing
      console.log(`Successfully joined game ${code}.`);
      // Navigate to the game room page
      navigate(`/game/${code}`);
    } catch (error) {
      console.error('Error joining game:', error);
      alert(`Failed to join game ${code}: ${error}`); // Basic error display
    } finally {
      setIsJoining(false);
      setCurrentJoinCode(''); // Clear tracking state
    }
  };

  /**
   * Handler for the manual private-join input.
   */
  const handleJoinPrivate = () => {
    const code = joinCode.trim();
    if (code) {
      handleJoin(code);
    }
  };

  // Show loading state while fetching initial lobby data AND isAuthenticated is null
   if (isLoadingInitial && isAuthenticated === null) {
      return <div className="flex items-center justify-center h-screen text-gray-900 dark:text-gray-100">Loading authentication and lobby...</div>;
   }

   // Show error if initial load failed or socket error occurred
    if (combinedError && isAuthenticated !== null) { // Only show error if auth state is resolved
        return (
             <div className="flex flex-col items-center justify-center h-screen text-red-600 dark:text-red-400">
                {/* Add PlayerInfoBanner here too, it will hide itself if not authenticated */}
                <PlayerInfoBanner />
                <h1 className="text-2xl font-bold mb-4">Error Loading Lobby</h1>
                <p>{combinedError.message}</p>
                {/* FIX: Use the refetchGames function from the hook */}
                <button onClick={refetchGames} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600" disabled={isLoadingInitial}>
                     {isLoadingInitial ? 'Loading...' : 'Try Again'}
                </button>
             </div>
        );
    }

    // Show loading state if initial load is still happening but auth is resolved
    if (isLoadingInitial && isAuthenticated !== null) {
         return (
             <div className="flex flex-col items-center justify-center h-screen text-gray-900 dark:text-gray-100">
                 <PlayerInfoBanner /> {/* Add banner while loading */}
                 Loading Lobby Games...
             </div>
         );
    }


    // If authenticated and lobby loaded, render lobby content
    // If not authenticated, AuthRedirect should handle redirecting to /login
    if (isAuthenticated !== true) {
        return null; // AuthRedirect will render the login page
    }


  return (
    <div style={{ padding: '1rem' }}>
       <PlayerInfoBanner /> {/* Render the banner at the top */}

      <h1>Lobby</h1>

        {/* Socket Connection Status Indicator */}
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: isSocketConnected ? 'green' : 'orange' }}>
            Server connection status: {isSocketConnected ? 'Connected' : 'Connecting...'}
             {socketError && <span style={{ color: 'red' }}> (Error: {socketError.message})</span>}
        </div>


      {/* Create game controls */}
      <div style={{ marginBottom: '1rem' }}>
        <button onClick={handleCreatePublic} style={{ marginRight: '1rem' }} disabled={isCreating || isJoining || !isSocketConnected}>
           {isCreating ? 'Creating Public Game...' : 'Create Public Game'}
        </button>
        <button onClick={() => setPrivateMode(!privateMode)} disabled={isCreating || isJoining || !isSocketConnected}>
          {privateMode ? 'Cancel Private Game' : 'Create Private Game'}
        </button>
        {privateMode && (
          <button onClick={handleCreatePrivate} style={{ marginLeft: '1rem' }} disabled={isCreating || isJoining || !isSocketConnected}>
            {isCreating ? 'Creating Private Game...' : 'Confirm Private Game'}
          </button>
        )}
      </div>

      {/* Private join section */}
      <div style={{ marginBottom: '2rem' }}>
        <h2>Join Private Game</h2>
        <input
          type="text"
          placeholder="Enter game code"
          value={joinCode}
          onChange={(e) => setJoinCode(e.target.value)}
          style={{ marginRight: '0.5rem' }}
          disabled={isCreating || isJoining || !isSocketConnected}
        />
        <button onClick={handleJoinPrivate} disabled={!joinCode.trim() || isCreating || isJoining || !isSocketConnected}>
           {isJoining && currentJoinCode === joinCode.trim() ? `Joining ${joinCode.trim()}...` : 'Join'}
        </button>
      </div>

      {/* Real-time waiting and started games lists */}
      <LobbyList title="Waiting Games" games={waitingGames} onJoin={handleJoin} />
      <LobbyList title="Started Games (Spectate Only)" games={startedGames} onJoin={handleJoin} />
    </div>
  );
};

export default Lobby;